import { Flex, Text } from '@chakra-ui/react';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Modal,
  Checkbox,
  Button,
  Pagination,
  SearchInput,
  Table,
  useToast
} from '@morressier/alembic';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addJournalBoardMembers } from 'src/api/mutations';
import { listUsers } from 'src/api/queries';
import ListLoader from 'src/components/ListLoader';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { pluralizeString } from 'src/utils/string';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journalId: string;
  organizationId: string;
}

export type IJournalBoardMembers = {
  userIds: string[];
};

const LIMIT = 10;

const AddBoardMembersModal = ({
  isOpen,
  onClose,
  journalId,
  organizationId
}: Props) => {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<
    Record<string, boolean>
  >({});

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'board-members-users-list',
      organizationId,
      journalId,
      searchValue,
      page
    ],
    queryFn: () =>
      listUsers({
        organizationId,
        submissionContextId: journalId,
        text: searchValue,
        offset: (page - 1) * LIMIT,
        limit: LIMIT
      }),
    enabled: !!organizationId,
    keepPreviousData: true
  });

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);
  const startIndex = (page - 1) * LIMIT + 1;
  const endIndex = Math.min(page * LIMIT, total);
  const items = data?.items ?? [];

  const addBoardMembersMutation = addJournalBoardMembers(journalId);

  const selectedCount = Object.values(selectedUserIds).filter(Boolean).length;

  const handleSearch = () => {
    setSearchValue(searchInput.trim());
    setPage(1);
  };

  const toggleMember = (userId: string) => {
    setSelectedUserIds(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleAddBoardMembers = async () => {
    const userIds = Object.entries(selectedUserIds)
      .filter(([, selected]) => selected)
      .map(([userId]) => userId);

    if (!userIds.length) return;

    const res = await addBoardMembersMutation.mutateAsync({ userIds });

    if (res?.isSuccess) {
      toast({
        variant: 'success',
        title: `${userIds.length} ${pluralizeString(
          'Board Member',
          userIds.length
        )} Added`,
        isClosable: true
      });
      onClose();
      return;
    }

    toast({
      variant: 'error',
      title: res?.message || res?.message?.message || 'Unexpected error.',
      isClosable: true
    });
  };

  const isTableLoading = isLoading || isFetching;

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="960px">
      <Flex direction="column" gap={6} p={8}>
        <Flex gap={3} direction="column" alignItems="flex-start">
          <Text textStyle="headingGroup">Add Board Members</Text>
          <Text textStyle="textBody">
            {isTableLoading ? (
              'Loading users...'
            ) : (
              <>
                Showing{' '}
                <Text as="span" fontWeight="bold">
                  {total === 0
                    ? total
                    : `${startIndex} - ${endIndex} of ${total}`}
                </Text>{' '}
                users can be added to this journal.
              </>
            )}
          </Text>

          <SearchInput
            placeholder="Search users by name and email"
            name="searchParams"
            value={searchInput}
            disabled={isTableLoading}
            onChange={e => setSearchInput(e.currentTarget.value)}
            onInputClear={() => {
              setSearchInput('');
              setSearchValue('');
              setPage(1);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
        </Flex>

        <Table>
          {!isTableLoading && items.length > 0 && (
            <Table.HeaderRow gap={2}>
              <Table.HeaderCell width="40px" />
              <Table.HeaderCell flex="1">Name</Table.HeaderCell>
              <Table.HeaderCell flex="1">Email</Table.HeaderCell>
            </Table.HeaderRow>
          )}
          <Table.Body>
            {isTableLoading ? (
              <ListLoader />
            ) : items.length === 0 ? (
              <SubmissionLoader
                title="No Users Found"
                subTitle="We could not find any users matching the provided name or email."
                bgColor="neutralSecondaryFill"
              />
            ) : (
              items.map(member => (
                <Table.Row key={member.id} gap={2}>
                  <Table.RowCell width="40px">
                    <Checkbox
                      name={`board-member-${member.id}`}
                      id={`board-member-${member.id}`}
                      ariaLabel="Select board member"
                      isChecked={!!selectedUserIds[member.id]}
                      onChange={() => toggleMember(member.id)}
                    >
                      {''}
                    </Checkbox>
                  </Table.RowCell>
                  <Table.RowCell flex="1">
                    {member.firstName} {member.lastName}
                  </Table.RowCell>
                  <Table.RowCell flex="1">{member.email}</Table.RowCell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>

        {totalPages > 1 && !isTableLoading && (
          <Flex justifyContent="center">
            <Pagination
              ariaLabel="Pagination"
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Flex>
        )}

        <Flex justifyContent="flex-end" gap={2}>
          <Button
            variant="secondary"
            onClick={onClose}
            isDisabled={addBoardMembersMutation.isLoading}
          >
            Exit Without Adding
          </Button>
          <Button
            variant="primary"
            iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
            onClick={handleAddBoardMembers}
            isLoading={addBoardMembersMutation.isLoading}
            isDisabled={!selectedCount || addBoardMembersMutation.isLoading}
          >
            Add Board Members {selectedCount > 0 && `(${selectedCount})`}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default AddBoardMembersModal;
